import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAppStore } from '../store/appStore';
import { ArrowLeft, User, Users, Lightbulb, BookOpen, Zap, Package, Mail, Bot, Building, Key, Shield } from 'lucide-react';

const RelationshipNetwork = () => {
  const { setCurrentPage } = useAppStore();
  const [selectedNode, setSelectedNode] = useState(null);
  const [hoveredNode, setHoveredNode] = useState(null);
  const containerRef = useRef(null);

  // 网络数据
  const networkData = {
    center: {
      id: 'jun-jie-zhang',
      name: 'Junjie Zhang',
      position: { x: 50, y: 50 },
      type: 'center'
    },
    projects: [
      {
        id: 'innovation-2024',
        name: '2024 Innovation Challenge',
        position: { x: 30, y: 20 },
        connections: ['chengan-yang', 'zhandong-lu'],
        color: '#FF6B6B',
        icon: Lightbulb
      },
      {
        id: 'rag-research',
        name: 'RAG Research',
        position: { x: 70, y: 15 },
        connections: ['frank-team'],
        color: '#4ECDC4',
        icon: BookOpen
      },
      {
        id: 'ai-workflow',
        name: 'AI Workflow',
        position: { x: 85, y: 35 },
        connections: ['frank-team'],
        color: '#45B7D1',
        icon: Zap
      },
      {
        id: 'bsd-cli',
        name: 'BSD CLI',
        position: { x: 80, y: 65 },
        connections: ['chengan-yang'],
        color: '#96CEB4',
        icon: Package
      },
      {
        id: 'email-assistant',
        name: 'Email Assistant',
        position: { x: 60, y: 85 },
        connections: ['chengan-yang', 'yono-ji', 'hanchen-peng'],
        color: '#FFEAA7',
        icon: Mail
      },
      {
        id: 'transformation-robot',
        name: 'Product Transformation Robot',
        position: { x: 30, y: 80 },
        connections: ['johnson-ma', 'fred-bao', 'hui-xu'],
        color: '#DDA0DD',
        icon: Bot
      },
      {
        id: 'iul-project',
        name: 'IUL Project',
        position: { x: 15, y: 60 },
        connections: ['ispeed-team', 'frank-team'],
        color: '#98D8C8',
        icon: Building
      },
      {
        id: 'singpass',
        name: 'Singpass Integration',
        position: { x: 20, y: 40 },
        connections: ['taiping-itd', 'ek-soft'],
        color: '#F7DC6F',
        icon: Key
      },
      {
        id: 'fapl',
        name: 'FAPL Integration',
        position: { x: 40, y: 15 },
        connections: ['fapl-team', 'taiping-user'],
        color: '#BB8FCE',
        icon: Shield
      },
      {
        id: 'tech-refresh',
        name: 'Tech Refresh Initiative',
        position: { x: 45, y: 82 },
        connections: ['qiankun-zhang', 'allen-gao'],
        color: '#FFA500',
        icon: Zap
      }
    ],
    people: [
      { id: 'chengan-yang', name: 'Chengan Yang', position: { x: 65, y: 25 }, type: 'person' },
      { id: 'zhandong-lu', name: 'Zhandong Lu', position: { x: 35, y: 35 }, type: 'person' },
      { id: 'yono-ji', name: 'Yono Ji', position: { x: 75, y: 75 }, type: 'person' },
      { id: 'hanchen-peng', name: 'Hanchen Peng', position: { x: 55, y: 70 }, type: 'person' },
      { id: 'johnson-ma', name: 'Johnson Ma', position: { x: 25, y: 65 }, type: 'person' },
      { id: 'fred-bao', name: 'Fred Bao', position: { x: 35, y: 70 }, type: 'person' },
      { id: 'hui-xu', name: 'Hui Xu', position: { x: 25, y: 75 }, type: 'person' },
      { id: 'qiankun-zhang', name: 'Qiankun Zhang', position: { x: 38, y: 88 }, type: 'person' },
      { id: 'allen-gao', name: 'Allen Gao', position: { x: 50, y: 88 }, type: 'person' }
    ],
    teams: [
      { id: 'frank-team', name: 'Frank Team', position: { x: 80, y: 25 }, type: 'team', category: 'internal' },
      { id: 'ispeed-team', name: 'iSpeed Team', position: { x: 15, y: 50 }, type: 'team', category: 'internal' },
      { id: 'taiping-itd', name: 'Taiping ITD', position: { x: 15, y: 30 }, type: 'team', category: 'external' },
      { id: 'ek-soft', name: 'EK Soft', position: { x: 25, y: 25 }, type: 'team', category: 'external' },
      { id: 'fapl-team', name: 'FAPL Team', position: { x: 45, y: 5 }, type: 'team', category: 'external' },
      { id: 'taiping-user', name: 'Taiping User', position: { x: 35, y: 10 }, type: 'team', category: 'external' }
    ]
  };

  // 获取连接线
  const getConnections = () => {
    const connections = [];
    
    networkData.projects.forEach(project => {
      connections.push({
        from: networkData.center,
        to: project,
        type: 'project-center'
      });
      
      project.connections.forEach(connectionId => {
        const person = networkData.people.find(p => p.id === connectionId);
        const team = networkData.teams.find(t => t.id === connectionId);
        const target = person || team;
        
        if (target) {
          connections.push({
            from: project,
            to: target,
            type: 'project-member'
          });
        }
      });
    });
    
    return connections;
  };

  const connections = getConnections();

  // 简化的节点尺寸百分比（基于Tailwind类名的实际效果）
  const getNodeSizePercent = (node, isCenter = false) => {
    // 假设基准字体大小为16px，容器宽度为1000px，高度为800px进行计算
    if (isCenter) return { w: 4, h: 8 }; // w-16 h-16: 64px/1000px=6.4%, 64px/800px=8%
    if (node.type === 'person') return { w: 2.5, h: 5 }; // w-10 h-10: 40px/1000px=4%, 40px/800px=5%
    if (node.type === 'team') return { w: 3, h: 6 }; // w-12 h-12: 48px/1000px=4.8%, 48px/800px=6%
    if (node.color) return { w: 1.5, h: 3 }; // w-6 h-6: 24px/1000px=2.4%, 24px/800px=3%
    return { w: 2, h: 4 }; // w-8 h-8: 32px/1000px=3.2%, 32px/800px=4%
  };

  // 使用SVG绘制连接线
  const ConnectionLine = ({ connection }) => {
    const isHighlighted = hoveredNode &&
      (connection.from.id === hoveredNode || connection.to.id === hoveredNode);

    // 获取节点尺寸
    const fromSize = getNodeSizePercent(connection.from, connection.from.id === 'jun-jie-zhang');
    const toSize = getNodeSizePercent(connection.to);

    // 计算节点实际的视觉中心（左上角 + 尺寸的一半）
    const fromCenterX = connection.from.position.x + (fromSize.w / 2);
    const fromCenterY = connection.from.position.y + (fromSize.h / 2);
    const toCenterX = connection.to.position.x + (toSize.w / 2);
    const toCenterY = connection.to.position.y + (toSize.h / 2);

    return (
      <>
        {/* 连接线连接到节点视觉中心 */}
        <line
          x1={`${fromCenterX}%`}
          y1={`${fromCenterY}%`}
          x2={`${toCenterX}%`}
          y2={`${toCenterY}%`}
          stroke={isHighlighted ? 'rgba(78, 205, 196, 0.8)' : 'rgba(255, 255, 255, 0.3)'}
          strokeWidth={isHighlighted ? '2' : '1'}
          strokeDasharray={connection.type === 'project-center' ? '5,5' : 'none'}
          className="transition-all duration-200"
        />
      </>
    );
  };

  const NetworkNode = ({ node, isCenter = false, style = {} }) => {
    const getNodeStyle = () => {
      if (isCenter) return 'w-16 h-16 bg-gradient-to-br from-yellow-400 to-orange-500';
      if (node.type === 'person') return 'w-10 h-10 bg-gradient-to-br from-blue-400 to-purple-500';
      if (node.type === 'team') {
        if (node.category === 'internal') {
          return 'w-12 h-12 bg-gradient-to-br from-green-400 to-teal-500';
        } else {
          return 'w-12 h-12 bg-gradient-to-br from-orange-400 to-red-500';
        }
      }
      return 'w-8 h-8 bg-gradient-to-br from-gray-400 to-gray-600';
    };

    const getIcon = () => {
      if (isCenter || node.type === 'person') return <User className="w-4 h-4 text-white" />;
      if (node.type === 'team') return <Users className="w-4 h-4 text-white" />;
      return null;
    };

    const isHighlighted = hoveredNode === node.id;

    return (
      <motion.div
        animate={{
          opacity: hoveredNode && !isHighlighted ? 0.3 : 1,
          scale: isHighlighted ? 1.3 : 1
        }}
        transition={{ duration: 0.2 }}
        className="absolute cursor-pointer"
        style={{
          left: `${node.position.x}%`,
          top: `${node.position.y}%`,
          transform: 'translate(-50%, -50%)',
          ...style
        }}
        onClick={() => setSelectedNode(node)}
        onMouseEnter={() => setHoveredNode(node.id)}
        onMouseLeave={() => setHoveredNode(null)}
      >
        <div className={`${getNodeStyle()} rounded-full flex items-center justify-center border-2 border-white/30 shadow-lg relative`}>
          {getIcon()}
        </div>

        <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 text-center">
          <div className="text-xs font-medium text-white whitespace-nowrap bg-black/50 px-2 py-1 rounded">
            {node.name}
          </div>
        </div>

        {isHighlighted && (
          <motion.div
            initial={{ scale: 1, opacity: 0 }}
            animate={{ scale: 2, opacity: 0.3 }}
            className={`absolute inset-0 ${getNodeStyle()} rounded-full blur-xl pointer-events-none`}
          />
        )}
      </motion.div>
    );
  };

  const NodeDetailModal = ({ node, onClose }) => {
    const getNodeIcon = () => {
      if (node.icon) {
        const Icon = node.icon;
        return <Icon className="w-8 h-8 text-white mb-2" />;
      }
      return null;
    };

    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="modal-overlay"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.8, opacity: 0 }}
          className="modal-content"
          onClick={(e) => e.stopPropagation()}
        >
          <button className="close-btn" onClick={onClose}>×</button>

          <div className="text-center">
            {/* 显示节点图标 */}
            {getNodeIcon()}
            <h2 className="text-2xl font-bold mb-4">{node.name}</h2>

            {node.id === 'jun-jie-zhang' && (
              <div>
                <p className="text-gray-300 mb-4">Core Coordinator - Connecting all projects and teams</p>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div className="bg-white/5 p-3 rounded">
                    <div className="font-bold">Projects</div>
                    <div className="text-2xl font-bold text-blue-400">9</div>
                  </div>
                  <div className="bg-white/5 p-3 rounded">
                    <div className="font-bold">Collaborators</div>
                    <div className="text-2xl font-bold text-green-400">7</div>
                  </div>
                  <div className="bg-white/5 p-3 rounded">
                    <div className="font-bold">Teams</div>
                    <div className="text-2xl font-bold text-purple-400">6</div>
                  </div>
                </div>
              </div>
            )}

            {node.type === 'person' && (
              <div>
                <p className="text-gray-300">Project Collaborator</p>
              </div>
            )}

            {node.type === 'team' && (
              <div>
                <p className="text-gray-300">
                  {node.category === 'internal' ? 'Internal Team' : 'External Team'}
                </p>
              </div>
            )}

            {node.color && (
              <div>
                <p className="text-gray-300 mb-2">Project Node</p>
                <div className="flex justify-center mb-2">
                  <div
                    className="w-12 h-12 rotate-45 border-2 border-white/40 flex items-center justify-center"
                    style={{ backgroundColor: node.color }}
                  >
                    <div className="-rotate-45">
                      {getNodeIcon()}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {node.connections && (
              <div className="mt-4">
                <h3 className="font-bold mb-2">Related Connections</h3>
                <div className="flex flex-wrap gap-2">
                  {node.connections.map(connectionId => {
                    const person = networkData.people.find(p => p.id === connectionId);
                    const team = networkData.teams.find(t => t.id === connectionId);
                    const target = person || team;
                    return target ? (
                      <span key={connectionId} className="px-2 py-1 bg-white/10 rounded text-sm">
                        {target.name}
                      </span>
                    ) : null;
                  })}
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    );
  };

  return (
    <div className="page" style={{ display: 'block' }}>
      <motion.button
        initial={{ opacity: 0, x: -50 }}
        animate={{ opacity: 1, x: 0 }}
        className="fixed top-8 left-8 z-10 p-3 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-white/20 transition-all"
        onClick={() => setCurrentPage('landing')}
      >
        <ArrowLeft className="w-6 h-6" />
      </motion.button>

      <motion.div
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        className="absolute top-8 left-1/2 transform -translate-x-1/2 text-center z-10"
      >
        <h1 className="text-4xl font-bold mb-2">Team Collaboration Network</h1>
        <p className="text-gray-300">Click nodes to view details</p>
      </motion.div>

      <div ref={containerRef} className="relative w-full h-full" style={{ zIndex: 1 }}>
        <svg
          className="absolute inset-0 w-full h-full pointer-events-none"
          style={{ zIndex: 1 }}
        >
          {connections.map((connection, index) => (
            <ConnectionLine key={index} connection={connection} />
          ))}
        </svg>
        <NetworkNode node={networkData.center} isCenter={true} style={{ zIndex: 2 }} />
        
        {networkData.projects.map(project => {
        const Icon = project.icon;
        return (
          <motion.div
            key={project.id}
            className="absolute cursor-pointer"
            style={{
              left: `${project.position.x}%`,
              top: `${project.position.y}%`,
              transform: 'translate(-50%, -50%)',
              zIndex: 2
            }}
            animate={{
              opacity: hoveredNode && hoveredNode !== project.id ? 0.3 : 1,
              scale: hoveredNode === project.id ? 1.2 : 1
            }}
            transition={{ duration: 0.2 }}
            onClick={() => setSelectedNode(project)}
            onMouseEnter={() => setHoveredNode(project.id)}
            onMouseLeave={() => setHoveredNode(null)}
          >
            {/* 项目节点：菱形设计 */}
            <div
              className="relative w-10 h-10 rotate-45 cursor-pointer border-2 border-white/40 shadow-lg hover:shadow-xl transition-shadow flex items-center justify-center"
              style={{ backgroundColor: project.color }}
            >
              {/* 图标容器 */}
              <div className="absolute inset-0 flex items-center justify-center -rotate-45 bg-black/20 rounded-sm">
                <Icon className="w-4 h-4 text-white" />
              </div>
            </div>
            {/* 项目名称 */}
            <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 text-center whitespace-nowrap">
              <div className="text-xs font-medium text-white bg-black/70 px-2 py-1 rounded">
                {project.name}
              </div>
            </div>
            {/* 悬停效果 */}
            {hoveredNode === project.id && (
              <motion.div
                initial={{ scale: 1, opacity: 0 }}
                animate={{ scale: 1.5, opacity: 0.3 }}
                className="absolute inset-0 w-10 h-10 rotate-45 pointer-events-none"
                style={{ backgroundColor: project.color }}
              />
            )}
          </motion.div>
        );
      })}
        
        {networkData.people.map(person => (
          <NetworkNode key={person.id} node={person} style={{ zIndex: 2 }} />
        ))}

        {networkData.teams.map(team => (
          <NetworkNode key={team.id} node={team} style={{ zIndex: 2 }} />
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0, x: 50 }}
        animate={{ opacity: 1, x: 0 }}
        className="fixed bottom-8 right-8 bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20"
        style={{ zIndex: 10 }}
      >
        <h3 className="font-bold mb-3">Legend</h3>
        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full"></div>
            <span>Core Person</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full"></div>
            <span>Collaborator</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-gradient-to-br from-green-400 to-teal-500 rounded-full"></div>
            <span>Internal Team</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-gradient-to-br from-orange-400 to-red-500 rounded-full"></div>
            <span>External Team</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rotate-45 border-2 border-white/30" style={{ backgroundColor: '#FF6B6B' }}></div>
            <span>Project Node</span>
          </div>
        </div>
      </motion.div>

      <AnimatePresence>
        {selectedNode && (
          <NodeDetailModal
            node={selectedNode}
            onClose={() => setSelectedNode(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default RelationshipNetwork;
